import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { storage } from '../../data/localStorage';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useAuth } from '../../contexts/AuthContext';
import { bloomsTaxonomy } from '../../data/bloomsTaxonomy';
import { vocabularyTiers } from '../../data/vocabularyTiers';
import { cefrLevels } from '../../data/cefrLevels';
import { questionService } from '../../services/questionService';
import { cloService } from '../../services/cloService';
import AddQuestionModal from './AddQuestionModal';
import EditQuestionModal from './EditQuestionModal';
import ExcelUploadModal from './ExcelUploadModal';

const { 
  FiPlus, FiSearch, FiFilter, FiEdit, FiTrash2, FiEye, FiLock, 
  FiTarget, FiLayers, FiUpload, FiBookOpen, FiGlobe 
} = FiIcons;

const QuestionBank = () => {
  const { hasPermission, currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [clos, setClos] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [filterCourseCode, setFilterCourseCode] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterDifficulty, setFilterDifficulty] = useState('');
  const [filterBloomLevel, setFilterBloomLevel] = useState('');
  const [filterVocabTier, setFilterVocabTier] = useState('');
  const [filterCefrLevel, setFilterCefrLevel] = useState(''); // New CEFR level filter
  const [isLoading, setIsLoading] = useState(true);

  const canManageQuestions = hasPermission('canManageQuestions');

  // Load questions on component mount
  useEffect(() => {
    loadQuestions();
    loadCLOs();
  }, [currentWorkspace?.id]);

  const loadQuestions = async () => {
    setIsLoading(true);
    try {
      if (currentWorkspace?.id) {
        const data = await questionService.getQuestions(currentWorkspace.id);
        setQuestions(data);
      } else {
        // Fallback to localStorage if no workspace
        const localQuestions = storage.getQuestions();
        setQuestions(localQuestions);
      }
    } catch (error) {
      console.error('Error loading questions:', error);
      // Fallback to localStorage
      const localQuestions = storage.getQuestions();
      setQuestions(localQuestions);
    } finally {
      setIsLoading(false);
    }
  };

  const loadCLOs = async () => {
    try {
      if (currentWorkspace?.id) {
        const data = await cloService.getCLOs(currentWorkspace.id);
        setClos(data);
      } else {
        const localCLOs = storage.getCLOs();
        setClos(localCLOs);
      }
    } catch (error) {
      console.error('Error loading CLOs:', error);
      const localCLOs = storage.getCLOs();
      setClos(localCLOs);
    }
  };

  const subjects = [...new Set(questions.map(q => q.subject))];
  const courseCodes = [...new Set(questions.map(q => q.courseCode).filter(Boolean))];
  const types = [...new Set(questions.map(q => q.type))];
  const difficulties = ['Easy', 'Medium', 'Hard'];
  const bloomLevels = Object.keys(bloomsTaxonomy);
  const vocabTiers = Object.keys(vocabularyTiers);
  const allCefrLevels = Object.keys(cefrLevels);

  const filteredQuestions = questions.filter(question => {
    return (
      question.text.toLowerCase().includes(searchTerm.toLowerCase()) &&
      (filterSubject === '' || question.subject === filterSubject) &&
      (filterCourseCode === '' || question.courseCode === filterCourseCode) &&
      (filterType === '' || question.type === filterType) &&
      (filterDifficulty === '' || question.difficulty === filterDifficulty) &&
      (filterBloomLevel === '' || question.bloomLevel === filterBloomLevel) &&
      (filterVocabTier === '' || question.vocabularyTier === filterVocabTier) &&
      (filterCefrLevel === '' || question.cefrLevel === filterCefrLevel)
    );
  });

  const handleAddQuestion = async (questionData) => {
    try {
      if (currentWorkspace?.id && user?.id) {
        await questionService.addQuestion(questionData, currentWorkspace.id, user.id);
      } else {
        storage.addQuestion(questionData);
      }
      await loadQuestions();
      setShowAddModal(false);
    } catch (error) {
      console.error('Error adding question:', error);
      alert('Error adding question. Please try again.');
    }
  };

  const handleEditQuestion = async (questionData) => {
    try {
      if (currentWorkspace?.id && selectedQuestion?.id) {
        await questionService.updateQuestion(selectedQuestion.id, questionData, currentWorkspace.id);
      } else {
        storage.updateQuestion(selectedQuestion.id, questionData);
      }
      await loadQuestions();
      setShowEditModal(false);
      setSelectedQuestion(null);
    } catch (error) {
      console.error('Error updating question:', error);
      alert('Error updating question. Please try again.');
    }
  };

  const handleDeleteQuestion = async (id) => {
    if (window.confirm('Are you sure you want to delete this question?')) {
      try {
        if (currentWorkspace?.id) {
          await questionService.deleteQuestion(id, currentWorkspace.id);
        } else {
          storage.deleteQuestion(id);
        }
        await loadQuestions();
      } catch (error) {
        console.error('Error deleting question:', error);
        alert('Error deleting question. Please try again.');
      }
    }
  };

  const handleExcelUpload = async (questionsData) => {
    try {
      if (currentWorkspace?.id && user?.id) {
        await questionService.addMultipleQuestions(questionsData, currentWorkspace.id, user.id);
      } else {
        questionsData.forEach(questionData => {
          storage.addQuestion(questionData);
        });
      }
      await loadQuestions();
      setShowExcelModal(false);
      alert(`Successfully uploaded ${questionsData.length} questions!`);
    } catch (error) {
      console.error('Error uploading questions:', error);
      alert('Error uploading questions. Please try again.');
    }
  };

  const openEditModal = (question) => {
    setSelectedQuestion(question);
    setShowEditModal(true);
  };

  const getBloomLevelDisplay = (levelKey) => {
    const level = bloomsTaxonomy[levelKey];
    if (!level) return null;
    return {
      label: level.label,
      color: level.color,
      level: level.level
    };
  };

  const getVocabTierDisplay = (tierKey) => {
    const tier = vocabularyTiers[tierKey];
    if (!tier) return null;
    return {
      label: tier.label,
      color: tier.color,
      level: tier.level
    };
  };

  const getCefrLevelDisplay = (levelKey) => {
    const level = cefrLevels[levelKey];
    if (!level) return null;
    return {
      name: level.name,
      color: level.color,
      level: level.level
    };
  };

  const getCLODisplay = (cloId) => {
    const clo = clos.find(c => c.id === cloId);
    if (!clo) return null;
    return {
      number: clo.cloNumber,
      description: clo.description,
      courseCode: clo.courseCode
    };
  };

  // Statistics
  const bloomStats = bloomLevels.reduce((acc, level) => {
    acc[level] = questions.filter(q => q.bloomLevel === level).length;
    return acc;
  }, {});

  const vocabStats = vocabTiers.reduce((acc, tier) => {
    acc[tier] = questions.filter(q => q.vocabularyTier === tier).length;
    return acc;
  }, {});

  const cefrStats = allCefrLevels.reduce((acc, level) => {
    acc[level] = questions.filter(q => q.cefrLevel === level).length;
    return acc;
  }, {});

  if (isLoading) {
    return (
      <div className="space-y-6 p-8 ml-72">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading questions...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-8 ml-72">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Question Bank</h2>
          <p className="text-gray-600 mt-1">
            {canManageQuestions 
              ? 'Manage your collection with Bloom\'s Taxonomy, Vocabulary Tier, and CEFR analysis'
              : 'Browse the collection of exam questions'
            }
          </p>
        </div>
        {canManageQuestions && (
          <div className="flex items-center space-x-3">
            <motion.button
              onClick={() => setShowExcelModal(true)}
              className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-green-700 transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <SafeIcon icon={FiUpload} />
              <span>Upload Excel</span>
            </motion.button>
            <motion.button
              onClick={() => setShowAddModal(true)}
              className="bg-primary-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-primary-700 transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <SafeIcon icon={FiPlus} />
              <span>Add Question</span>
            </motion.button>
          </div>
        )}
      </div>

      {/* Analysis Statistics */}
      {questions.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Bloom's Taxonomy Distribution */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
            <div className="flex items-center space-x-2 mb-4">
              <SafeIcon icon={FiTarget} className="text-primary-600" />
              <h3 className="text-lg font-semibold text-gray-800">Bloom's Taxonomy Distribution</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {bloomLevels.map(levelKey => {
                const level = bloomsTaxonomy[levelKey];
                const count = bloomStats[levelKey];
                const percentage = questions.length > 0 ? Math.round((count / questions.length) * 100) : 0;
                return (
                  <div key={levelKey} className="text-center">
                    <div className={`p-3 rounded-lg ${level.color.replace('text-', 'bg-').replace('-800', '-100')}`}>
                      <div className="text-lg font-bold text-gray-800">{count}</div>
                      <div className="text-xs text-gray-600">{percentage}%</div>
                    </div>
                    <div className="mt-2">
                      <div className="text-xs font-medium text-gray-700">Level {level.level}</div>
                      <div className="text-xs text-gray-500">{level.label.split(' ')[0]}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Vocabulary Tier Distribution */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
            <div className="flex items-center space-x-2 mb-4">
              <SafeIcon icon={FiLayers} className="text-primary-600" />
              <h3 className="text-lg font-semibold text-gray-800">Vocabulary Tier Distribution</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {vocabTiers.map(tierKey => {
                const tier = vocabularyTiers[tierKey];
                const count = vocabStats[tierKey];
                const percentage = questions.length > 0 ? Math.round((count / questions.length) * 100) : 0;
                return (
                  <div key={tierKey} className="text-center">
                    <div className={`p-3 rounded-lg ${tier.color.replace('text-', 'bg-').replace('-800', '-100')}`}>
                      <div className="text-lg font-bold text-gray-800">{count}</div>
                      <div className="text-xs text-gray-600">{percentage}%</div>
                    </div>
                    <div className="mt-2">
                      <div className="text-xs font-medium text-gray-700">{tier.label.split(' ')[0]}</div>
                      <div className="text-xs text-gray-500">{tier.vocabularyLevel}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* CEFR Level Distribution */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
            <div className="flex items-center space-x-2 mb-4">
              <SafeIcon icon={FiGlobe} className="text-primary-600" />
              <h3 className="text-lg font-semibold text-gray-800">CEFR Level Distribution</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {allCefrLevels.map(levelKey => {
                const level = cefrLevels[levelKey];
                const count = cefrStats[levelKey] || 0;
                const percentage = questions.length > 0 ? Math.round((count / questions.length) * 100) : 0;
                return (
                  <div key={levelKey} className="text-center">
                    <div className={`p-3 rounded-lg ${level.bgColor}`}>
                      <div className="text-lg font-bold text-gray-800">{count}</div>
                      <div className="text-xs text-gray-600">{percentage}%</div>
                    </div>
                    <div className="mt-2">
                      <div className="text-xs font-medium text-gray-700">{levelKey}</div>
                      <div className="text-xs text-gray-500">{level.name}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Permission Notice */}
      {!canManageQuestions && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <SafeIcon icon={FiLock} className="text-blue-600" />
            <p className="text-blue-800 text-sm">
              You have view-only access to the question bank. Contact an admin to add or edit questions.
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-8 gap-4">
          <div className="relative">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search questions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <select
            value={filterSubject}
            onChange={(e) => setFilterSubject(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All Subjects</option>
            {subjects.map(subject => (
              <option key={subject} value={subject}>{subject}</option>
            ))}
          </select>

          <select
            value={filterCourseCode}
            onChange={(e) => setFilterCourseCode(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All Courses</option>
            {courseCodes.map(code => (
              <option key={code} value={code}>{code}</option>
            ))}
          </select>

          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All Types</option>
            {types.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>

          <select
            value={filterDifficulty}
            onChange={(e) => setFilterDifficulty(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All Difficulties</option>
            {difficulties.map(difficulty => (
              <option key={difficulty} value={difficulty}>{difficulty}</option>
            ))}
          </select>

          <select
            value={filterBloomLevel}
            onChange={(e) => setFilterBloomLevel(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All Bloom Levels</option>
            {bloomLevels.map(levelKey => {
              const level = bloomsTaxonomy[levelKey];
              return (
                <option key={levelKey} value={levelKey}>
                  Level {level.level}: {level.label.split(' ')[0]}
                </option>
              );
            })}
          </select>

          <select
            value={filterVocabTier}
            onChange={(e) => setFilterVocabTier(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All Vocab Tiers</option>
            {vocabTiers.map(tierKey => {
              const tier = vocabularyTiers[tierKey];
              return (
                <option key={tierKey} value={tierKey}>
                  {tier.label}
                </option>
              );
            })}
          </select>

          {/* CEFR Level Filter */}
          <select
            value={filterCefrLevel}
            onChange={(e) => setFilterCefrLevel(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All CEFR Levels</option>
            {allCefrLevels.map(levelKey => {
              const level = cefrLevels[levelKey];
              return (
                <option key={levelKey} value={levelKey}>
                  {levelKey}: {level.name}
                </option>
              );
            })}
          </select>

          <button
            onClick={() => {
              setSearchTerm('');
              setFilterSubject('');
              setFilterCourseCode('');
              setFilterType('');
              setFilterDifficulty('');
              setFilterBloomLevel('');
              setFilterVocabTier('');
              setFilterCefrLevel('');
            }}
            className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <SafeIcon icon={FiFilter} className="mr-2" />
            Clear
          </button>
        </div>
      </div>

      {/* Questions List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800">
            Questions ({filteredQuestions.length})
          </h3>
        </div>
        <div className="divide-y divide-gray-100">
          <AnimatePresence>
            {filteredQuestions.map((question, index) => {
              const bloomDisplay = getBloomLevelDisplay(question.bloomLevel);
              const vocabDisplay = getVocabTierDisplay(question.vocabularyTier);
              const cefrDisplay = getCefrLevelDisplay(question.cefrLevel);
              const cloDisplay = getCLODisplay(question.cloId);

              return (
                <motion.div
                  key={question.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ delay: index * 0.05 }}
                  className="p-6 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-gray-800 font-medium mb-3">{question.text}</p>
                      <div className="flex items-center space-x-3 text-sm text-gray-600 flex-wrap gap-2">
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                          {question.subject}
                        </span>
                        
                        {question.courseCode && (
                          <span className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full">
                            <SafeIcon icon={FiBookOpen} className="inline mr-1 text-xs" />
                            {question.courseCode}
                          </span>
                        )}

                        {cloDisplay && (
                          <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs" title={cloDisplay.description}>
                            <SafeIcon icon={FiTarget} className="inline mr-1 text-xs" />
                            {cloDisplay.number}
                          </span>
                        )}

                        <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded-full">
                          {question.type}
                        </span>

                        <span className={`px-2 py-1 rounded-full ${
                          question.difficulty === 'Easy' 
                            ? 'bg-green-100 text-green-800' 
                            : question.difficulty === 'Medium' 
                            ? 'bg-yellow-100 text-yellow-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {question.difficulty}
                        </span>

                        {bloomDisplay && (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${bloomDisplay.color}`}>
                            <SafeIcon icon={FiTarget} className="inline mr-1 text-xs" />
                            L{bloomDisplay.level}: {bloomDisplay.label.split(' ')[0]}
                          </span>
                        )}

                        {vocabDisplay && (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${vocabDisplay.color}`}>
                            <SafeIcon icon={FiLayers} className="inline mr-1 text-xs" />
                            {vocabDisplay.label.split(' ')[0]}
                          </span>
                        )}

                        {/* CEFR Level Badge */}
                        {cefrDisplay && (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${cefrDisplay.color}`}>
                            <SafeIcon icon={FiGlobe} className="inline mr-1 text-xs" />
                            {question.cefrLevel}: {cefrDisplay.name}
                          </span>
                        )}

                        <span className="font-medium text-gray-700">{question.marks} marks</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      {canManageQuestions ? (
                        <>
                          <button
                            onClick={() => openEditModal(question)}
                            className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                          >
                            <SafeIcon icon={FiEdit} />
                          </button>
                          <button
                            onClick={() => handleDeleteQuestion(question.id)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <SafeIcon icon={FiTrash2} />
                          </button>
                        </>
                      ) : (
                        <button className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors">
                          <SafeIcon icon={FiEye} />
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>

          {filteredQuestions.length === 0 && (
            <div className="p-12 text-center">
              <SafeIcon icon={FiEye} className="mx-auto text-4xl text-gray-300 mb-4" />
              <p className="text-gray-500">No questions found matching your criteria</p>
            </div>
          )}
        </div>
      </div>

      {/* Modals - Only show if user has permissions */}
      {canManageQuestions && (
        <>
          <AddQuestionModal
            isOpen={showAddModal}
            onClose={() => setShowAddModal(false)}
            onSubmit={handleAddQuestion}
          />
          <EditQuestionModal
            isOpen={showEditModal}
            onClose={() => setShowEditModal(false)}
            onSubmit={handleEditQuestion}
            question={selectedQuestion}
          />
          <ExcelUploadModal
            isOpen={showExcelModal}
            onClose={() => setShowExcelModal(false)}
            onUpload={handleExcelUpload}
          />
        </>
      )}
    </div>
  );
};

export default QuestionBank;